/* z-search.js — zulily.com search intent router (theme asset).
 *
 * v2, 2026-10-01. Everything v1 did is unchanged and runs FIRST: whole-query redirect keys and
 * intent rules from snippets/z-search-config.liquid (hand-curated keys always win), gender + size
 * extraction with filter params, predictive suggestion, fail-open to native search.
 *
 * New in v2 — the composition tier, fed by window.ZSearchData (rendered by the snippet from the
 * shop metafields custom.z_search_vendors + custom.z_search_targets, both rebuilt nightly by the
 * z-search-vocab edge function):
 *   tokens are matched in ANY order after punctuation/accent/plural folding:
 *     "nike boys shorts" = "boys nike shorts" = "Nike, boys' short" = "nikes boys shorts"
 *   brand (nightly vendor map) + model (style collections) + gender + type (canonical leaf
 *   dictionary with synonyms) + size are extracted; what is left decides exactness.
 *   exact (nothing left over)  -> hard redirect to the best target:
 *     dedicated collection (combo / hub / model, when it holds >= floor products)
 *     else brand collection + product_type filter + gender filters
 *     else type collection + gender filters
 *   partial (leftover words)   -> no redirect; the predictive dropdown offers the composed target.
 *   kids: "boys" = Boys OR Unisex Kids, "girls" = Girls OR Unisex Kids (repeated filter params),
 *   because youth product is mostly gendered Unisex Kids.
 * v3 (same day): every submit sends one beacon (query text + the router's decision, no customer
 * data) to the z-search-log edge function for the weekly unrouted report; the composed suggestion
 * now renders inside Dawn's search modal (a banner above the predictive results).
 * Pure functions are exported for Node tests (scripts/site-search/zsearch.test.mjs).
 */
(function (root) {
  const config = root.ZSearchConfig || {};
  const settings = config.settings || {};
  const synonyms = config.synonyms || {};
  const replacements = config.replacements || [];
  const exactQueryRewrites = config.exactQueryRewrites || {};
  const collectionIntentRules = config.collectionIntentRules || [];
  const predictiveConfig = config.predictive || {};
  const data = root.ZSearchData || {};
  const targets = data.targets || {};
  const types = targets.types || {};
  const FLOOR = targets.floor || 3;
  // Whole-query redirect map = nightly vendor map overlaid by the hand-curated keys (hand wins).
  // The snippet no longer has to loop the vendor map into the config block, so each page carries
  // the map once, inside ZSearchData.
  const redirects = Object.assign({}, data.vendors || {}, config.redirects || {});
  // Brand vocabulary for the composition tier (filtered below, once the word lists exist).
  let vendors = data.vendors || {};

  const DEBUG = !!settings.debug;
  const MIN_PREDICTIVE_CHARS = settings.minPredictiveChars || 2;
  const PREDICTIVE_DEBOUNCE_MS = settings.predictiveDebounceMs || 220;
  const SIZE_FILTER_PARAM = "filter.v.m.custom.primary_size";
  const GENDER_FILTER_PARAM = "filter.p.m.my_fields.gender";
  const TYPE_FILTER_PARAM = "filter.p.product_type";

  let predictiveAbortController = null;

  function log() {
    if (!DEBUG) return;
    console.log("[z-search]", ...arguments);
  }

  /* ───────────────────────── v1 whole-query engine (unchanged) ───────────────────────── */

  function normalizeQuery(raw) {
    let q = String(raw || "").toLowerCase();
    replacements.forEach(function (rule) {
      try { q = q.replace(new RegExp(rule.pattern, rule.flags || ""), rule.replace); }
      catch (e) { log("Replacement rule failed", rule, e); }
    });
    if (synonyms[q]) q = synonyms[q];
    if (exactQueryRewrites[q]) q = exactQueryRewrites[q];
    return q;
  }

  function getQueryVariants(normalizedQuery) {
    const variants = [];
    const q = String(normalizedQuery || "").trim();
    if (!q) return variants;
    variants.push(q);
    if (q.endsWith("s") && q.length > 1) variants.push(q.slice(0, -1));
    return Array.from(new Set(variants));
  }

  function ruleMatches(query, rule) {
    if (!rule || !rule.matchType || !rule.query || !rule.redirect) return false;
    if (rule.matchType === "exact") return query === rule.query;
    if (rule.matchType === "startsWith") return query.indexOf(rule.query) === 0;
    if (rule.matchType === "contains") return query.indexOf(rule.query) !== -1;
    return false;
  }

  function getRuleScore(rule) {
    if (!rule || !rule.matchType || !rule.query) return 0;
    if (rule.matchType === "exact") return 3000 + rule.query.length;
    if (rule.matchType === "startsWith") return 2000 + rule.query.length;
    if (rule.matchType === "contains") return 1000 + rule.query.length;
    return rule.query.length;
  }

  function extractSize(normalizedQuery) {
    const q = String(normalizedQuery || "").trim();
    if (!q) return null;
    const numericMatch = q.match(/(?:^|\b)(?:size\s*)?(3[5-9]|4[0-9]|50)(?=\b|$)/);
    if (numericMatch) return numericMatch[1];
    const alphaPatterns = [
      { regex: /(?:^|\b)(?:size\s*)?(xxxl|3xl)(?=\b|$)/, value: "3XL" },
      { regex: /(?:^|\b)(?:size\s*)?(xxl|2xl)(?=\b|$)/, value: "2XL" },
      { regex: /(?:^|\b)(?:size\s*)?(xl|x-large|extra large)(?=\b|$)/, value: "XL" },
      { regex: /(?:^|\b)(?:size\s*)?(large|lg|l)(?=\b|$)/, value: "L" },
      { regex: /(?:^|\b)(?:size\s*)?(medium|med|m)(?=\b|$)/, value: "M" },
      { regex: /(?:^|\b)(?:size\s*)?(small|sm|s)(?=\b|$)/, value: "S" },
      { regex: /(?:^|\b)(?:size\s*)?(xs|x-small|extra small)(?=\b|$)/, value: "XS" },
      { regex: /(?:^|\b)(?:size\s*)?(xxs|2xs)(?=\b|$)/, value: "2XS" }
    ];
    for (let i = 0; i < alphaPatterns.length; i++) if (alphaPatterns[i].regex.test(q)) return alphaPatterns[i].value;
    return null;
  }

  function stripSizeFromQuery(normalizedQuery) {
    let q = String(normalizedQuery || "");
    q = q.replace(/\bsize\s*(3[5-9]|4[0-9]|50)\b/g, " ");
    q = q.replace(/\b(3[5-9]|4[0-9]|50)\b/g, " ");
    q = q.replace(/\bsize\s*(xxxl|3xl|xxl|2xl|xl|x-large|extra large|large|lg|l|medium|med|m|small|sm|s|xs|x-small|extra small|xxs|2xs)\b/g, " ");
    q = q.replace(/\b(xxxl|3xl|xxl|2xl|xl|x-large|extra large|large|lg|l|medium|med|m|small|sm|s|xs|x-small|extra small|xxs|2xs)\b/g, " ");
    return q.replace(/\s+/g, " ").trim();
  }

  function extractGender(normalizedQuery) {
    const q = String(normalizedQuery || "").trim();
    if (!q) return null;
    const genderPatterns = [
      { regex: /(?:^|\b)(baby girls)(?=\b|$)/, value: "Baby Girls" },
      { regex: /(?:^|\b)(baby boys)(?=\b|$)/, value: "Baby Boys" },
      { regex: /(?:^|\b)(unisex kids|kids unisex)(?=\b|$)/, value: "Unisex Kids" },
      { regex: /(?:^|\b)(baby)(?=\b|$)/, value: "Baby" },
      { regex: /(?:^|\b)(juniors|junior)(?=\b|$)/, value: "Juniors" },
      { regex: /(?:^|\b)(womens|women's|women)(?=\b|$)/, value: "Womens" },
      { regex: /(?:^|\b)(mens|men's|men)(?=\b|$)/, value: "Mens" },
      { regex: /(?:^|\b)(girls|girl)(?=\b|$)/, value: "Girls" },
      { regex: /(?:^|\b)(boys|boy)(?=\b|$)/, value: "Boys" },
      { regex: /(?:^|\b)(unisex)(?=\b|$)/, value: "Unisex" }
    ];
    for (let i = 0; i < genderPatterns.length; i++) if (genderPatterns[i].regex.test(q)) return genderPatterns[i].value;
    return null;
  }

  function stripGenderFromQuery(normalizedQuery) {
    let q = String(normalizedQuery || "");
    q = q.replace(/\b(baby girls|baby boys|unisex kids|kids unisex|juniors|junior|womens|women's|women|mens|men's|men|girls|girl|boys|boy|unisex|baby)\b/g, " ");
    return q.replace(/\s+/g, " ").trim();
  }

  // v1 gender value -> the my_fields.gender filter values that must be ORed. Youth product is
  // gendered "Unisex Kids" far more often than Boys/Girls (Nike: 385 vs 75), so a boys/girls
  // search that filtered on the single value landed on near-empty pages.
  const V1_GENDER_FILTERS = {
    "Womens": ["Womens"], "Mens": ["Mens"], "Unisex": ["Unisex"], "Baby": ["Baby"],
    "Baby Girls": ["Baby"], "Baby Boys": ["Baby"], "Juniors": ["Womens"],
    "Girls": ["Girls", "Unisex Kids"], "Boys": ["Boys", "Unisex Kids"], "Unisex Kids": ["Unisex Kids", "Girls", "Boys"]
  };

  function isAlphaSize(sizeValue) { return ["2XS", "XS", "S", "M", "L", "XL", "2XL", "3XL"].indexOf(sizeValue) !== -1; }
  function isNumericSize(sizeValue) { return /^\d+(\.5)?$/.test(String(sizeValue || "")); }

  const SIZE_ELIGIBLE_PREFIXES = [
    "/collections/birkenstock", "/collections/birkenstock-", "/collections/shoes", "/collections/womens-shoes", "/collections/mens-shoes",
    "/collections/sandals", "/collections/boots", "/collections/sneakers", "/collections/slides", "/collections/nike", "/collections/nike-shoes",
    "/collections/adidas", "/collections/adidas-shoes", "/collections/new-balance", "/collections/new-balance-shoes", "/collections/hoka",
    "/collections/asics", "/collections/puma", "/collections/reebok", "/collections/vionic", "/collections/brooks", "/collections/merrell",
    "/collections/crocs", "/collections/clarks", "/collections/spenco", "/collections/jbu", "/collections/ugg", "/collections/toms",
    "/collections/ryka", "/collections/franco-sarto", "/collections/fly-london", "/collections/vuori", "/collections/columbia"
  ];
  const APPAREL_KEYWORDS = ["shirt", "shirts", "hoodie", "hoodies", "top", "tops", "jacket", "jackets", "pants", "shorts", "leggings", "sweater", "sweaters",
    "sweatshirt", "sweatshirts", "jogger", "joggers", "polo", "polos", "blouse", "blouses", "tunic", "tunics", "dress", "dresses", "clothing", "apparel"];
  const FOOTWEAR_KEYWORDS = ["shoe", "shoes", "sandal", "sandals", "boot", "boots", "sneaker", "sneakers", "slide", "slides", "clog", "clogs",
    "birkenstock", "arizona", "boston", "gizeh", "mayari", "madrid", "milano", "siena", "franca"];

  function isSizeFilterEligibleRedirect(redirectUrl, baseQuery, sizeValue) {
    for (let i = 0; i < SIZE_ELIGIBLE_PREFIXES.length; i++) {
      if (redirectUrl.indexOf(SIZE_ELIGIBLE_PREFIXES[i]) === 0) {
        if (isNumericSize(sizeValue)) return true;
        if (isAlphaSize(sizeValue)) {
          const queryText = String(baseQuery || "");
          for (let j = 0; j < APPAREL_KEYWORDS.length; j++) if (queryText.indexOf(APPAREL_KEYWORDS[j]) !== -1) return true;
          for (let k = 0; k < FOOTWEAR_KEYWORDS.length; k++) if (queryText.indexOf(FOOTWEAR_KEYWORDS[k]) !== -1) return true;
          if (redirectUrl.indexOf("/collections/nike") === 0 || redirectUrl.indexOf("/collections/adidas") === 0 ||
              redirectUrl.indexOf("/collections/new-balance") === 0 || redirectUrl.indexOf("/collections/vuori") === 0 ||
              redirectUrl.indexOf("/collections/columbia") === 0) return true;
        }
      }
    }
    return false;
  }

  const GENDER_ELIGIBLE_PREFIXES = SIZE_ELIGIBLE_PREFIXES.concat([
    "/collections/shirts", "/collections/womens-tops", "/collections/tunics", "/collections/pullovers", "/collections/fleece", "/collections/hoodies",
    "/collections/jackets", "/collections/dresses", "/collections/maxi-dresses", "/collections/plus-size-dresses", "/collections/pants", "/collections/shorts",
    "/collections/jeans", "/collections/leggings", "/collections/skirts", "/collections/sweatpants", "/collections/womens-clothing", "/collections/girls-clothing",
    "/collections/maternity"
  ]);

  function isGenderFilterEligibleRedirect(redirectUrl) {
    for (let i = 0; i < GENDER_ELIGIBLE_PREFIXES.length; i++) if (redirectUrl.indexOf(GENDER_ELIGIBLE_PREFIXES[i]) === 0) return true;
    // v2: any nightly brand collection, and any type collection whose leaf is in a gendered department
    const path = redirectUrl.split("?")[0];
    for (const k in vendors) if (vendors[k] === path) return true;
    return false;
  }

  function buildUrl(path, opts) {
    const params = [];
    params.push("sort_by=best-selling");
    if (opts && opts.type) params.push(TYPE_FILTER_PARAM + "=" + encodeURIComponent(opts.type));
    if (opts && opts.genders) for (let i = 0; i < opts.genders.length; i++) params.push(GENDER_FILTER_PARAM + "=" + encodeURIComponent(opts.genders[i]));
    if (opts && opts.size) params.push(SIZE_FILTER_PARAM + "=" + encodeURIComponent(opts.size));
    return path + "?" + params.join("&");
  }

  function appendFilters(redirectUrl, size, gender) {
    const path = redirectUrl.split("?")[0];
    const genders = gender ? (V1_GENDER_FILTERS[gender] || [gender]) : null;
    return buildUrl(path, { genders: genders, size: size });
  }

  function findBaseRedirect(normalizedQuery) {
    const queryVariants = getQueryVariants(normalizedQuery);
    for (let i = 0; i < queryVariants.length; i++) if (redirects[queryVariants[i]]) return redirects[queryVariants[i]];
    const matchedRules = [];
    for (let i = 0; i < collectionIntentRules.length; i++) {
      const rule = collectionIntentRules[i];
      for (let j = 0; j < queryVariants.length; j++) {
        if (ruleMatches(queryVariants[j], rule)) { matchedRules.push({ rule: rule, score: getRuleScore(rule) }); break; }
      }
    }
    if (matchedRules.length > 0) {
      matchedRules.sort(function (a, b) { return b.score - a.score; });
      return matchedRules[0].rule.redirect;
    }
    return null;
  }

  /* ───────────────────────── v2 composition tier ───────────────────────── */

  // Same normalization as z-search-vocab's normalizeKey (vendor keys) and the type dictionary.
  function fold(s) {
    let k = String(s || "").toLowerCase();
    try { k = k.normalize("NFKD").replace(/[̀-ͯ]/g, ""); } catch (e) { /* old browsers: keep as is */ }
    k = k.replace(/&/g, " and ").replace(/\+/g, " plus ");
    k = k.replace(/'s\b/g, "s").replace(/[.'’‘,!?®™"()\[\]:;]/g, "").replace(/[^\x00-\x7f]/g, "");
    k = k.replace(/[-_\/]/g, " ").replace(/\s+/g, " ").trim();
    return k;
  }
  function tokenize(q) { return fold(q).split(" ").filter(Boolean); }
  function singular(w) {
    if (w.length <= 3) return w;
    if (/ies$/.test(w)) return w.slice(0, -3) + "y";
    if (/(ches|shes|sses|xes)$/.test(w)) return w.slice(0, -2);
    if (/s$/.test(w) && !/ss$/.test(w)) return w.slice(0, -1);
    return w;
  }

  const GENDER_TOKENS = {
    womens: "womens", women: "womens", woman: "womens", womans: "womens", ladies: "womens", lady: "womens", female: "womens", females: "womens",
    mens: "mens", men: "mens", man: "mens", mans: "mens", male: "mens", males: "mens", guys: "mens", guy: "mens",
    girls: "girls", girl: "girls", boys: "boys", boy: "boys",
    kids: "kids", kid: "kids", kiddos: "kids", children: "kids", childrens: "kids", child: "kids", childs: "kids", youth: "kids", youths: "kids",
    toddler: "kids", toddlers: "kids", junior: "kids", juniors: "kids", tween: "kids", tweens: "kids", teen: "kids", teens: "kids",
    baby: "baby", babies: "baby", infant: "baby", infants: "baby", newborn: "baby", newborns: "baby", unisex: "unisex"
  };
  const GENDER_FILTERS = {
    womens: ["Womens"], mens: ["Mens"], unisex: ["Unisex"], baby: ["Baby"],
    girls: ["Girls", "Unisex Kids"], boys: ["Boys", "Unisex Kids"], kids: ["Girls", "Boys", "Unisex Kids"]
  };
  const GENDER_LABEL = { womens: "Women's", mens: "Men's", girls: "Girls'", boys: "Boys'", kids: "Kids'", baby: "Baby", unisex: "Unisex" };
  const STOPWORDS = { for: 1, and: 1, the: 1, with: 1, in: 1, of: 1, on: 1, a: 1, an: 1, to: 1, my: 1, me: 1, sale: 1, sales: 1, deal: 1, deals: 1,
    cheap: 1, new: 1, best: 1, size: 1, sized: 1, shop: 1, buy: 1, discount: 1, clearance: 1, plus: 1 };
  // the nightly vendor map minus anything that is also a gender word or stopword (defensive —
  // the generator excludes these already)
  vendors = (function () {
    const out = {}; const src = data.vendors || {};
    for (const k in src) if (!GENDER_TOKENS[k] && !STOPWORDS[k]) out[k] = src[k];
    return out;
  })();
  const ALPHA_SIZES = { xxs: "2XS", "2xs": "2XS", xs: "XS", s: "S", small: "S", sm: "S", m: "M", medium: "M", med: "M", l: "L", large: "L", lg: "L",
    xl: "XL", xxl: "2XL", "2xl": "2XL", xxxl: "3XL", "3xl": "3XL" };

  // indexes built once from ZSearchData
  const typeIndex = (function () {
    const idx = {};   // folded phrase -> leaf
    for (const leaf in types) {
      const t = types[leaf]; const forms = [fold(leaf)].concat(t.s || []);
      for (let i = 0; i < forms.length; i++) {
        const f = fold(forms[i]); if (!f) continue;
        if (!idx[f] || idx[f].length < leaf.length) idx[f] = leaf;
        const sg = f.split(" ").map(singular).join(" "); if (!idx[sg]) idx[sg] = leaf;
      }
    }
    return idx;
  })();
  const modelIndex = (function () {   // vendorKey -> { modelKey: [handle,count] }
    const idx = {}; const m = targets.models || {};
    for (const k in m) { const p = k.split("|"); if (!idx[p[0]]) idx[p[0]] = {}; idx[p[0]][p[1]] = m[k]; }
    return idx;
  })();

  function matchPhrase(tokens, lookup, maxN) {
    // longest n-gram first; ties -> rightmost; tries the exact phrase, then singularized tokens
    for (let n = Math.min(maxN, tokens.length); n >= 1; n--) {
      for (let i = tokens.length - n; i >= 0; i--) {
        const slice = tokens.slice(i, i + n);
        const phrase = slice.join(" "); const sg = slice.map(singular).join(" ");
        const hit = lookup(phrase) || lookup(sg) || (n === 1 ? lookup(phrase + "s") : null);
        if (hit) return { i: i, n: n, hit: hit, phrase: phrase };
      }
    }
    return null;
  }

  function parse(raw) {
    let tokens = tokenize(raw);
    const out = { gender: null, size: null, brand: null, brandKey: null, model: null, modelKey: null, leaf: null, leftover: [] };
    // size: "size 10", "10", "10.5", "4-16 US / 35-50 EU", alpha sizes ("xl", "size m"; bare s/m/l only after "size")
    const kept = [];
    for (let i = 0; i < tokens.length; i++) {
      const t = tokens[i];
      if (t === "size" && i + 1 < tokens.length && (/^\d{1,2}(\.5)?$/.test(tokens[i + 1]) || ALPHA_SIZES[tokens[i + 1]])) {
        const v = tokens[i + 1]; out.size = ALPHA_SIZES[v] || v; i++; continue;
      }
      if (/^\d{1,2}(\.5)?$/.test(t) && parseFloat(t) >= 4 && parseFloat(t) <= 50 && !out.size) { out.size = t; continue; }
      if (ALPHA_SIZES[t] && t.length >= 2 && !out.size) { out.size = ALPHA_SIZES[t]; continue; }
      kept.push(t);
    }
    tokens = kept;
    // gender: any position; girls+boys together = kids; baby wins over girls/boys ("baby girl")
    const genders = []; const rest = [];
    for (let i = 0; i < tokens.length; i++) { const g = GENDER_TOKENS[tokens[i]]; if (g) genders.push(g); else rest.push(tokens[i]); }
    if (genders.length) {
      if (genders.indexOf("baby") !== -1) out.gender = "baby";
      else if (genders.indexOf("girls") !== -1 && genders.indexOf("boys") !== -1) out.gender = "kids";
      else if (genders.indexOf("kids") !== -1 && (genders.indexOf("girls") !== -1 || genders.indexOf("boys") !== -1)) out.gender = genders.indexOf("girls") !== -1 ? "girls" : "boys";
      else out.gender = genders[0];
    }
    tokens = rest;
    // brand
    const b = matchPhrase(tokens, function (p) { return vendors[p] ? p : null; }, 4);
    if (b) {
      let key = b.hit;   // "nikes" is a plural alias of "nike": use the canonical key so combo/model lookups match
      if (/s$/.test(key) && vendors[key.slice(0, -1)] === vendors[key]) key = key.slice(0, -1);
      if (targets.aliases && targets.aliases[key]) key = targets.aliases[key];   // "jordan" -> "air jordan", "altra" -> "altra running"
      out.brandKey = key; out.brand = vendors[key]; tokens = tokens.slice(0, b.i).concat(tokens.slice(b.i + b.n));
    }
    // model (for the found brand, or any brand's model which then implies the brand)
    const modelLookup = function (p) {
      if (out.brandKey) return modelIndex[out.brandKey] && modelIndex[out.brandKey][p] ? out.brandKey + "|" + p : null;
      for (const v in modelIndex) if (modelIndex[v][p]) return v + "|" + p;
      return null;
    };
    const m = matchPhrase(tokens, modelLookup, 3);
    if (m) {
      const p = m.hit.split("|"); out.modelKey = m.hit; out.model = p[1];
      if (!out.brandKey) { out.brandKey = p[0]; out.brand = vendors[p[0]] || null; }
      tokens = tokens.slice(0, m.i).concat(tokens.slice(m.i + m.n));
    }
    // type
    const t = matchPhrase(tokens, function (p) { return typeIndex[p] || null; }, 3);
    if (t) { out.leaf = t.hit; tokens = tokens.slice(0, t.i).concat(tokens.slice(t.i + t.n)); }
    out.leftover = tokens.filter(function (w) { return !STOPWORDS[w]; });
    out.exact = out.leftover.length === 0 && !!(out.brandKey || out.leaf || out.modelKey);
    return out;
  }

  function ok(target) { return target && target[1] >= FLOOR ? "/collections/" + target[0] : null; }

  function compose(p) {
    const hubs = targets.hubs || {}, combos = targets.combos || {}, models = targets.models || {};
    const leafKey = p.leaf ? fold(p.leaf) : null;
    const gendered = p.leaf ? !!(types[p.leaf] && types[p.leaf].g) : true;
    // a department word (shoes, clothing, jewelry) is never a product_type value: dedicated
    // collection or the brand page with gender filters only
    const isDept = p.leaf ? (types[p.leaf] && fold(types[p.leaf].l1) === leafKey) : false;
    const genders = p.gender ? GENDER_FILTERS[p.gender] : null;
    const g = p.gender || "";
    const label = [p.brand ? titleCase(p.brandKey) : "", p.model ? titleCase(p.model) : "", p.gender ? GENDER_LABEL[p.gender] : "", p.leaf || ""].filter(Boolean).join(" ");
    let url = null;
    if (p.modelKey) {
      const h = ok(models[p.modelKey]);
      if (h) url = buildUrl(h, { genders: genders, size: p.size });
      else if (p.brand) url = buildUrl(p.brand, { genders: genders, size: p.size });
    } else if (p.brandKey && p.leaf) {
      const dedicated = (g && ok(combos[p.brandKey + "|" + g + "|" + leafKey])) || null;
      const unisexCombo = ok(combos[p.brandKey + "||" + leafKey]);
      if (dedicated) url = buildUrl(dedicated, { size: p.size });
      else if (unisexCombo) url = buildUrl(unisexCombo, { genders: gendered ? genders : null, size: p.size });
      else if (p.brand) url = buildUrl(p.brand, { type: isDept ? null : p.leaf, genders: gendered ? genders : null, size: p.size });
    } else if (p.brandKey) {
      url = p.brand ? buildUrl(p.brand, { genders: genders, size: p.size }) : null;
    } else if (p.leaf && p.gender) {
      // "kids" never falls back to a single-gender hub (it would silently exclude the other half):
      // a kids hub, else the type collection with Girls + Boys + Unisex Kids filters
      const hub = ok(hubs[g + "|" + leafKey]);
      if (hub && gendered) url = buildUrl(hub, { size: p.size });
      else {
        const typeColl = redirects[leafKey] || redirects[singular(leafKey)] || redirects[fold(p.leaf)];
        if (typeColl) url = buildUrl(typeColl.split("?")[0], { genders: gendered ? genders : null, size: p.size });
      }
    } else if (p.leaf) {
      const typeColl = redirects[leafKey] || redirects[singular(leafKey)];
      if (typeColl) url = typeColl;
    }
    return url ? { url: url, label: label, exact: p.exact, parsed: p } : null;
  }

  function titleCase(s) { return String(s || "").split(" ").map(function (w) { return w ? w[0].toUpperCase() + w.slice(1) : w; }).join(" "); }

  function composeQuery(rawQuery) {
    try { if (!data.vendors && !data.targets) return null; return compose(parse(rawQuery)); }
    catch (e) { log("compose failed (fail open)", e); return null; }
  }

  // hub upgrade for the v1 path: "boys shorts" resolved by v1 to /collections/shorts + gender filter,
  // but a dedicated boys-shorts hub exists -> prefer it.
  function hubFor(gender, baseQuery) {
    const g = gender ? ({ "Womens": "womens", "Mens": "mens", "Girls": "girls", "Boys": "boys", "Unisex Kids": "kids", "Baby": "baby", "Baby Girls": "baby", "Baby Boys": "baby" })[gender] : null;
    if (!g) return null;
    const leaf = typeIndex[fold(baseQuery)] || typeIndex[fold(baseQuery).split(" ").map(singular).join(" ")];
    if (!leaf) return null;
    const hubs = targets.hubs || {}; const lk = fold(leaf);
    return ok(hubs[g + "|" + lk]);
  }

  // Full decision for a normalized query: { url, tier, parsed }.
  //   tier: v1-map | v1-rule | v2-exact (hard redirects) | suggestion (composed, leftover words) | none
  function resolve(normalizedQuery) {
    const size = extractSize(normalizedQuery);
    const gender = extractGender(normalizedQuery);
    let baseQuery = normalizedQuery;
    if (size) baseQuery = stripSizeFromQuery(baseQuery);
    if (gender) baseQuery = stripGenderFromQuery(baseQuery);
    const baseRedirect = findBaseRedirect(baseQuery);
    if (baseRedirect) {
      const tier = (redirects[baseQuery] === baseRedirect || redirects[baseQuery.replace(/s$/, "")] === baseRedirect) ? "v1-map" : "v1-rule";
      const hub = hubFor(gender, baseQuery);
      if (hub && tier === "v1-map") return { url: buildUrl(hub, { size: size && isSizeFilterEligibleRedirect(baseRedirect, baseQuery, size) ? size : null }), tier: tier, label: normalizedQuery };
      const applySize = size && isSizeFilterEligibleRedirect(baseRedirect, baseQuery, size);
      // v2: a hand key for a gendered type word ("shorts" -> some shorts collection) takes the gender filter too
      const baseLeaf = typeIndex[fold(baseQuery)] || typeIndex[fold(baseQuery).split(" ").map(singular).join(" ")];
      const applyGender = gender && (isGenderFilterEligibleRedirect(baseRedirect) || !!(baseLeaf && types[baseLeaf] && types[baseLeaf].g));
      const url = (applySize || applyGender) ? appendFilters(baseRedirect, applySize ? size : null, applyGender ? gender : null) : baseRedirect;
      return { url: url, tier: tier, label: normalizedQuery };
    }
    // v2: composition tier — hard redirect only on an exact parse, otherwise a suggestion
    const c = composeQuery(normalizedQuery);
    if (c && c.exact) return { url: c.url, tier: "v2-exact", label: c.label, parsed: c.parsed };
    if (c) return { url: null, suggestion: c.url, tier: "suggestion", label: c.label, parsed: c.parsed };
    let parsed = null;
    try { parsed = parse(normalizedQuery); } catch (e) { /* ignore */ }
    return { url: null, tier: "none", parsed: parsed };
  }

  function findRedirect(normalizedQuery) { return resolve(normalizedQuery).url; }

  function findSuggestion(normalizedQuery) {
    const r = resolve(normalizedQuery);
    if (r.url) return { url: r.url, label: r.label || normalizedQuery };
    if (r.suggestion) return { url: r.suggestion, label: r.label };
    return null;
  }

  /* ───────────────────────── beacon (Phase 4) ───────────────────────── */

  // One small POST per search submit: the query and the router's decision, nothing about the
  // shopper. settings.logEndpoint = "" in the snippet turns it off.
  const LOG_ENDPOINT = (settings.logEndpoint === undefined) ? "https://jnpzqyejootzwhwepzer.supabase.co/functions/v1/z-search-log" : settings.logEndpoint;

  // One submit reaches tryRedirectFromForm up to three times (keydown Enter, submit, button
  // click), so the same query within 2 s is logged once.
  let lastLogged = { q: null, at: 0 };
  function logSearch(rawQuery, decision) {
    try {
      if (!LOG_ENDPOINT || typeof navigator === "undefined" || !navigator.sendBeacon) return;
      const key = String(rawQuery || "").trim().toLowerCase(); const now = Date.now();
      if (key === lastLogged.q && now - lastLogged.at < 2000) return;
      lastLogged = { q: key, at: now };
      const p = decision.parsed || {};
      const payload = {
        q: String(rawQuery || "").trim().slice(0, 120), normalized: String(normalizeQuery(rawQuery) || "").slice(0, 120),
        tier: decision.tier, target: (decision.url || decision.suggestion || "").slice(0, 300),
        brand: p.brandKey || null, gender: p.gender || null, leaf: p.leaf || null, model: p.model || null, size: p.size || null,
        leftover: (p.leftover || []).join(" ").slice(0, 120) || null,
        page: ((root.location && root.location.pathname) || "").slice(0, 200)
      };
      navigator.sendBeacon(LOG_ENDPOINT, new Blob([JSON.stringify(payload)], { type: "text/plain" }));
    } catch (e) { log("beacon failed (ignored)", e); }
  }

  /* ───────────────────────── DOM wiring (unchanged from v1) ───────────────────────── */

  function getSearchInputFromForm(form) { return form ? form.querySelector('input[name="q"], input[type="search"]') : null; }

  function isLikelySearchForm(form) {
    if (!form || form.tagName !== "FORM") return false;
    const action = form.getAttribute("action") || "";
    const hasQueryInput = !!form.querySelector('input[name="q"], input[type="search"]');
    return hasQueryInput && (action.indexOf("/search") !== -1 || action === "" || form.hasAttribute("data-predictive-search") ||
      form.className.toLowerCase().indexOf("search") !== -1 || form.getAttribute("role") === "search");
  }

  function getRedirectForRawQuery(rawQuery) {
    const normalizedQuery = normalizeQuery(rawQuery);
    const decision = resolve(normalizedQuery);
    logSearch(rawQuery, decision);
    return { normalizedQuery: normalizedQuery, redirectUrl: decision.url };
  }

  function tryRedirectFromForm(form, event) {
    if (!isLikelySearchForm(form)) return false;
    const queryInput = getSearchInputFromForm(form);
    if (!queryInput) return false;
    const result = getRedirectForRawQuery(queryInput.value);
    log("Form redirect check", { originalQuery: queryInput.value, normalizedQuery: result.normalizedQuery, redirectUrl: result.redirectUrl });
    queryInput.value = result.normalizedQuery;
    if (result.redirectUrl) {
      if (event) { event.preventDefault(); event.stopPropagation(); }
      root.location.assign(result.redirectUrl);
      return true;
    }
    ensureSearchTypeParams(form);
    return false;
  }

  function handleSearchSubmit(event) {
    const form = event.target;
    if (!form || form.tagName !== "FORM") return;
    tryRedirectFromForm(form, event);
  }

  function ensureSearchTypeParams(form) {
    if (!form.querySelector('input[name="type"]')) {
      const input = document.createElement("input");
      input.type = "hidden"; input.name = "type"; input.value = "product,collection";
      form.appendChild(input);
    }
  }

  function debounce(fn, wait) {
    let timeout;
    return function () { const context = this, args = arguments; clearTimeout(timeout); timeout = setTimeout(function () { fn.apply(context, args); }, wait); };
  }

  function fetchPredictiveSearch(query, callback) {
    if (!predictiveConfig.enabled) return;
    if (query.length < MIN_PREDICTIVE_CHARS) { callback(null, ""); return; }
    if (predictiveAbortController) predictiveAbortController.abort();
    predictiveAbortController = new AbortController();
    const resourceTypes = (predictiveConfig.resourceTypes || ["product", "collection", "query"]).join(",");
    const limit = predictiveConfig.limit || 8;
    const url = "/search/suggest?q=" + encodeURIComponent(query) + "&resources[type]=" + encodeURIComponent(resourceTypes) +
      "&resources[limit]=" + encodeURIComponent(limit) + "&section_id=predictive-search";
    log("Predictive fetch", url);
    fetch(url, { signal: predictiveAbortController.signal, credentials: "same-origin" })
      .then(function (response) { if (!response.ok) throw new Error("Predictive search request failed"); return response.text(); })
      .then(function (html) { callback(null, html); })
      .catch(function (error) { if (error.name === "AbortError") return; callback(error); });
  }

  function getPredictiveContainer(input) {
    const explicitTarget = input.getAttribute("data-predictive-target");
    if (explicitTarget) return document.querySelector(explicitTarget);
    const parent = input.closest("[data-search-root]");
    if (parent) return parent.querySelector("[data-predictive-results]");
    return document.querySelector("[data-predictive-results]");
  }

  // Dawn's <predictive-search> rewrites the innerHTML of [data-predictive-search] on every fetch,
  // so the suggestion lives in its own element just above that panel and is managed here.
  function getSuggestionBanner(input, create) {
    const host = input.closest("predictive-search") || input.closest("form");
    if (!host) return null;
    let el = host.querySelector(".z-search-redirect-suggestion");
    if (!el && create) {
      el = document.createElement("div");
      el.className = "z-search-redirect-suggestion";
      el.setAttribute("style", "margin:.6rem 0 0;padding:.75rem 1rem;border:1px solid rgba(var(--color-foreground),.12);border-radius:.4rem;font-size:1.4rem;");
      const panel = host.querySelector("[data-predictive-search]");
      if (panel && panel.parentNode) panel.parentNode.insertBefore(el, panel); else host.appendChild(el);
    }
    return el;
  }

  function renderSuggestion(input, suggestion) {
    const legacy = getPredictiveContainer(input);
    if (legacy) {   // themes that provide their own container keep the v1 behaviour
      if (!suggestion) { legacy.innerHTML = ""; legacy.hidden = true; return; }
      legacy.innerHTML = '<div class="z-search-redirect-suggestion"><a href="' + suggestion.url + '">Shop <strong>' + escapeHtml(suggestion.label) + '</strong></a></div>';
      legacy.hidden = false; return;
    }
    const el = getSuggestionBanner(input, !!suggestion);
    if (!el) return;
    if (!suggestion) { el.hidden = true; el.innerHTML = ""; return; }
    el.innerHTML = '<a href="' + suggestion.url + '" style="text-decoration:none;color:rgb(var(--color-foreground));">Shop <strong>' + escapeHtml(suggestion.label) + '</strong> &rarr;</a>';
    el.hidden = false;
  }

  function renderPredictiveResults(input, html) {
    const container = getPredictiveContainer(input);
    if (!container) return;
    if (!html) { container.innerHTML = ""; container.hidden = true; return; }
    container.innerHTML = html; container.hidden = false;
  }

  const handlePredictiveInput = debounce(function (event) {
    const input = event.target;
    if (!input || (input.name !== "q" && input.type !== "search")) return;
    const form = input.closest("form");
    if (!isLikelySearchForm(form)) return;
    const normalizedQuery = normalizeQuery(input.value);
    const suggestion = normalizedQuery.length >= MIN_PREDICTIVE_CHARS ? findSuggestion(normalizedQuery) : null;
    log("Predictive input", { value: input.value, normalizedQuery: normalizedQuery, suggestion: suggestion });
    renderSuggestion(input, suggestion);
    if (!getPredictiveContainer(input)) return;   // Dawn fetches its own predictive results; the banner sits above them
    if (suggestion) return;                       // legacy container: suggestion replaces results
    fetchPredictiveSearch(normalizedQuery, function (error, html) {
      if (error) { log("Predictive search failed", error); return; }
      renderPredictiveResults(input, html);
    });
  }, PREDICTIVE_DEBOUNCE_MS);

  function handleEnterKey(event) {
    if (event.key !== "Enter") return;
    const target = event.target;
    if (!target || !(target.matches('input[name="q"]') || target.matches('input[type="search"]'))) return;
    const form = target.closest("form");
    if (form) tryRedirectFromForm(form, event);
  }

  function handleSearchButtonClick(event) {
    const button = event.target.closest('button[type="submit"], input[type="submit"]');
    if (!button) return;
    const form = button.closest("form");
    if (form) tryRedirectFromForm(form, event);
  }

  function escapeHtml(str) {
    return String(str).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#039;");
  }

  function init() {
    document.addEventListener("submit", handleSearchSubmit, true);
    document.addEventListener("input", handlePredictiveInput);
    document.addEventListener("keydown", handleEnterKey, true);
    document.addEventListener("click", handleSearchButtonClick, true);
    document.addEventListener("click", function (event) {
      const results = document.querySelector("[data-predictive-results]");
      if (!results) return;
      if (!event.target.closest("[data-search-root]") && !results.contains(event.target)) results.hidden = true;
    });
    log("Initialized", { vendors: Object.keys(vendors).length, types: Object.keys(types).length });
  }

  if (typeof document !== "undefined") {
    if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init); else init();
  }
  // Node tests
  if (typeof module !== "undefined" && module.exports) {
    module.exports = { normalizeQuery: normalizeQuery, findRedirect: findRedirect, findSuggestion: findSuggestion, resolve: resolve, parse: parse, compose: compose, fold: fold };
  }
})(typeof window !== "undefined" ? window : globalThis);
